import { TextArea } from "@king-ict/eupisi-ui/components";
import { useTranslation } from "react-i18next";

interface IProps {
  value: string | undefined;
  maxLength: number;
  onChange: (value: string) => void;
  label?: string;
  ariaLabel?: string;
  /**
   * Name attribute for the textarea
   */
  name?: string;
  /**
   * Number of rows to display
   * @default 4
   */
  rows?: number;
  readonly?: boolean;
  disabled?: boolean;
  errorText?: string;
  /**
   * Additional helper text to display before the character count
   */
  helperTextPrefix?: string;
  /**
   * Whether to automatically adjust rows based on content length
   * @default false
   */
  autoAdjustRows?: boolean;
  /**
   * Base number of characters per row for auto-adjustment
   * @default 100
   */
  charsPerRow?: number;
  className?: string;
}

export function TextAreaWithCount({
  value = "",
  maxLength,
  onChange,
  label,
  ariaLabel,
  name,
  rows = 4,
  readonly,
  disabled,
  errorText,
  helperTextPrefix,
  autoAdjustRows = false,
  charsPerRow = 100,
  className,
}: IProps) {
  const { t } = useTranslation();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const exceededMaxLength = value.length > maxLength;
  const calculatedRows = autoAdjustRows
    ? Math.ceil(value.length / charsPerRow)
    : rows;
  const helperText = `${helperTextPrefix ? `${helperTextPrefix} • ` : ""}${
    value.length
  } / ${maxLength}`;

  return (
    <TextArea
      ariaLabel={ariaLabel || label || ""}
      label={label}
      name={name}
      value={value}
      readonly={readonly}
      disabled={disabled}
      onChange={handleChange}
      rows={calculatedRows}
      maxLength={maxLength}
      errorText={
        exceededMaxLength ? t("validations.maxLengthError") : errorText
      }
      helperText={helperText}
      className={className}
    />
  );
}
